import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtEur } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useMarginParams, calcMargine, costoStimato } from "@/lib/margine";

const isDemo = (n) => /demoliz|smaltim|rimoz/i.test(n || "");
const groupOf = (v) => (isDemo(v.name) ? "DEMOLIZIONI" : v.category || "ALTRO");
const GROUP_ORDER = ["DEMOLIZIONI", "MURATURA", "IMPIANTI", "INFISSI", "SERVIZI"];
const defaultMode = (unit) => (["pz", "punto", "forfait", "cad"].includes(unit) ? "fissa" : "mq");

export const calcQty = (it, mq) => {
  if (it.qty_mode === "fissa") return Number(it.qty_value) || 0;
  if (it.qty_mode === "ml") return (Number(it.qty_ratio) || 0) * mq * 0.4;
  return (Number(it.qty_ratio) || 0) * mq;
};

const toDraft = (packages) => {
  const d = {};
  packages.forEach((p) => {
    d[p.id] = {};
    (p.items || []).forEach((it) => {
      d[p.id][it.voce_id || it.id] = { qty_mode: it.qty_mode || "mq", qty_ratio: it.qty_ratio ?? 0, qty_value: it.qty_value ?? 0, unit_price_pkg: it.unit_price_pkg ?? null, brochure_testo: it.brochure_testo || "" };
    });
  });
  return d;
};

// Stesso ricavo del Preventivo Pacchetto: €/mq × mq (maggiorazione <60 mq) + finiture listini incluse, oppure prezzo fisso
export function ricavoPacchetto(p, mq) {
  if (Number(p.price_override) > 0) return Number(p.price_override);
  const [mqEff, mult] = mq < 40 ? [40, 1.15] : mq < 60 ? [mq, 1.10] : [mq, 1];
  const listiniRiv = (p.listini_items || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.prezzo_rivendita) || 0), 0);
  return (p.price_per_m2 || 0) * mqEff * mult + listiniRiv;
}

function pkgTotals(p, items, voci, mq, params) {
  const ricavo = ricavoPacchetto(p, mq);
  let costo = 0;
  Object.entries(items || {}).forEach(([vid, it]) => {
    const v = voci[vid];
    if (v) costo += calcQty(it, mq) * ((v.prezzo_acquisto || 0) > 0 ? v.prezzo_acquisto : costoStimato(v.prezzo_rivendita));
  });
  const listini = (p.listini_items || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.prezzo_netto) || Number(li.prezzo_acquisto) || costoStimato(li.prezzo_rivendita)), 0);
  costo += listini;
  const m = calcMargine(ricavo, costo, params);
  return { ricavo, costo, listini, indiretti: m ? m.indiretti + m.provvigione : 0, margine: m ? m.utile : ricavo - costo, pct: m ? m.pct : 0, giudizio: m?.giudizio, n: Object.keys(items || {}).length };
}

function PkgSummary({ p, t, dirty }) {
  const cls = t.giudizio === "buono" ? "text-emerald-600" : t.giudizio === "attenzione" ? "text-amber-600" : "text-rose-600";
  return (
    <div className="bg-white border rounded-lg p-3 min-w-0" style={{ borderColor: p.color || "#d4d4d8", borderTopWidth: 4 }} data-testid={`matrix-summary-${p.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-bold truncate" style={{ color: p.color }}>{p.name}</div>
        {dirty && <span className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">modificato</span>}
      </div>
      <div className="text-[11px] text-zinc-500">{fmtEur(p.price_per_m2)}/mq · {t.n} voci</div>
      <div className="grid grid-cols-2 gap-x-2 mt-2 text-xs">
        <span className="text-zinc-500">Ricavo</span><span className="text-right font-mono">{fmtEur(t.ricavo)}</span>
        <span className="text-zinc-500">Costi diretti</span><span className="text-right font-mono">{fmtEur(t.costo)}</span>
        <span className="text-zinc-500">Costi indiretti</span><span className="text-right font-mono">{fmtEur(t.indiretti)}</span>
        <span className="text-zinc-500">Utile</span><span className="text-right font-mono">{fmtEur(t.margine)}</span>
      </div>
      <div className={`mt-2 text-2xl font-extrabold font-mono ${t.n === 0 ? "text-zinc-400" : cls}`} data-testid={`matrix-margin-${p.id}`}>
        {t.n === 0 ? "—" : `${t.pct.toFixed(1)}%`}
      </div>
      {t.n === 0 && <div className="text-[10px] text-amber-700" data-testid={`matrix-empty-${p.id}`}>Nessuna voce: aggiungile qui sotto</div>}
      {t.listini > 0 && <div className="text-[10px] text-zinc-400">incl. {fmtEur(t.listini)} prodotti listini</div>}
    </div>
  );
}

function MatrixCell({ p, v, it, mq, ricavo, onToggle, onChange }) {
  const checked = !!it;
  const costo = checked ? calcQty(it, mq) * (v.prezzo_acquisto || 0) : 0;
  const inc = ricavo ? (costo / ricavo) * 100 : 0;
  const tid = `${p.id}-${v.id}`;
  return (
    <td className={`px-2 py-1.5 align-top border-l border-zinc-100 ${checked ? "bg-emerald-50/60" : ""}`}>
      <div className="flex items-center gap-1.5">
        <input type="checkbox" checked={checked} onChange={onToggle} className="h-4 w-4 accent-emerald-600 cursor-pointer" data-testid={`matrix-check-${tid}`} />
        {checked && (
          <>
            <select value={it.qty_mode} onChange={(e) => onChange("qty_mode", e.target.value)} className="h-7 border border-zinc-300 rounded text-[11px] px-1 bg-white" data-testid={`matrix-mode-${tid}`}>
              <option value="fissa">Fissa</option>
              <option value="mq">× mq</option>
              <option value="ml">× ml</option>
            </select>
            <Input type="number" step="0.01" min={0} className="h-7 w-16 text-xs text-right px-1"
              value={it.qty_mode === "fissa" ? it.qty_value : it.qty_ratio}
              onFocus={(e) => e.target.select()}
              onChange={(e) => onChange(it.qty_mode === "fissa" ? "qty_value" : "qty_ratio", e.target.value === "" ? "" : Number(e.target.value))}
              data-testid={`matrix-qty-${tid}`} />
          </>
        )}
      </div>
      {checked && (
        <div className="text-[10px] mt-1 text-zinc-500 font-mono" data-testid={`matrix-impact-${tid}`}>
          {calcQty(it, mq).toFixed(1)} {v.unit} · {fmtEur(costo)} · <span className={inc > 5 ? "text-rose-600 font-semibold" : "text-zinc-600"}>−{inc.toFixed(2)}% marg.</span>
        </div>
      )}
    </td>
  );
}

export default function PacchettiMatrix({ packages, voci, reload }) {
  const [draft, setDraft] = useState(() => toDraft(packages));
  const [dirty, setDirty] = useState({});
  const [mq, setMq] = useState(70);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [onlyIncluded, setOnlyIncluded] = useState(false);
  const [saving, setSaving] = useState(false);
  const vociMap = useMemo(() => Object.fromEntries(voci.map((v) => [v.id, v])), [voci]);
  const groups = useMemo(() => [...new Set([...GROUP_ORDER, ...voci.map(groupOf)])].filter((g) => voci.some((v) => groupOf(v) === g)), [voci]);
  const marginParams = useMarginParams("semplice");
  const totals = Object.fromEntries(packages.map((p) => [p.id, pkgTotals(p, draft[p.id], vociMap, mq, marginParams)]));

  const rows = voci.filter((v) => {
    if (q && !`${v.name} ${v.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (cat && groupOf(v) !== cat) return false;
    if (onlyIncluded && !packages.some((p) => draft[p.id]?.[v.id])) return false;
    return true;
  });

  const mark = (pid) => setDirty((d) => ({ ...d, [pid]: true }));
  const toggle = (pid, v) => {
    setDraft((d) => {
      const cur = { ...(d[pid] || {}) };
      if (cur[v.id]) delete cur[v.id];
      else cur[v.id] = { qty_mode: defaultMode(v.unit), qty_ratio: 1, qty_value: 1, unit_price_pkg: null };
      return { ...d, [pid]: cur };
    });
    mark(pid);
  };
  const change = (pid, vid, k, val) => {
    setDraft((d) => ({ ...d, [pid]: { ...d[pid], [vid]: { ...d[pid][vid], [k]: val } } }));
    mark(pid);
  };
  const reset = () => { setDraft(toDraft(packages)); setDirty({}); };

  const save = async () => {
    setSaving(true);
    try {
      const ids = Object.keys(dirty).filter((k) => dirty[k]);
      await Promise.all(ids.map((pid) => api.put(`/packages/${pid}`, {
        items: Object.entries(draft[pid] || {}).map(([voce_id, it]) => ({ voce_id, qty_mode: it.qty_mode, qty_ratio: Number(it.qty_ratio) || 0, qty_value: Number(it.qty_value) || 0, unit_price_pkg: it.unit_price_pkg, brochure_testo: it.brochure_testo || "" })),
      })));
      toast.success(`${ids.length} pacchett${ids.length === 1 ? "o salvato" : "i salvati"}`);
      setDirty({});
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const nDirty = Object.values(dirty).filter(Boolean).length;
  if (!packages.length) return <div className="text-zinc-500">Nessun pacchetto</div>;

  return (
    <div data-testid="pacchetti-matrix">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-zinc-400" />
          <Input placeholder="Cerca voce…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9 w-64" data-testid="matrix-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 border border-zinc-300 rounded px-2 text-sm" data-testid="matrix-cat-filter">
          <option value="">Tutte le categorie</option>
          {groups.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={onlyIncluded} onChange={(e) => setOnlyIncluded(e.target.checked)} data-testid="matrix-only-included" /> Solo voci incluse
        </label>
        <div className="flex items-center gap-1.5 text-sm ml-2">
          Simula su <Input type="number" min={1} value={mq} onChange={(e) => setMq(Math.max(1, Number(e.target.value) || 1))} className="h-9 w-20 text-right" data-testid="matrix-mq" /> mq
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={reset} disabled={!nDirty} data-testid="matrix-reset"><RotateCcw className="h-4 w-4 mr-1" />Annulla</Button>
          <Button onClick={save} disabled={!nDirty || saving} data-testid="matrix-save" style={{ background: "var(--brand)", color: "white" }}>
            <Save className="h-4 w-4 mr-1" />{saving ? "Salvataggio…" : `Salva${nDirty ? ` (${nDirty})` : ""}`}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: `minmax(240px,1.3fr) repeat(${packages.length}, minmax(180px,1fr))` }}>
        <div className="text-xs text-zinc-500 self-end leading-relaxed">
          Spunta in quale pacchetto va ogni voce e la quantità. <strong>Fissa</strong> = quantità totale, <strong>× mq / × ml</strong> = coefficiente sui metri della casa.
          Sotto ogni cella vedi quanto la voce pesa sul margine a {mq} mq.
        </div>
        {packages.map((p) => <PkgSummary key={p.id} p={p} t={totals[p.id]} dirty={dirty[p.id]} />)}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-auto max-h-[65vh]">
        <table className="w-full text-sm table-fixed min-w-[1100px]">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left w-[300px]">Voce</th>
              {packages.map((p) => <th key={p.id} className="px-2 py-2 text-left border-l border-zinc-200" style={{ color: p.color }}>{p.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const gRows = rows.filter((v) => groupOf(v) === g);
              if (!gRows.length) return null;
              return (
                <React.Fragment key={g}>
                  <tr className="bg-zinc-100"><td colSpan={packages.length + 1} className="px-3 py-1 text-[11px] font-bold tracking-wider text-zinc-600">{g} <span className="font-normal text-zinc-400">({gRows.length})</span></td></tr>
                  {gRows.map((v) => (
                    <tr key={v.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`matrix-row-${v.id}`}>
                      <td className="px-3 py-1.5 align-top">
                        <div className="font-medium leading-tight">{v.name}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">costo {Number(v.prezzo_acquisto || 0).toFixed(2)}€ · vendita {Number(v.prezzo_rivendita || 0).toFixed(2)}€ /{v.unit}</div>
                      </td>
                      {packages.map((p) => (
                        <MatrixCell key={p.id} p={p} v={v} it={draft[p.id]?.[v.id]} mq={mq} ricavo={totals[p.id].ricavo}
                          onToggle={() => toggle(p.id, v)} onChange={(k, val) => change(p.id, v.id, k, val)} />
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {!rows.length && <tr><td colSpan={packages.length + 1} className="text-center py-10 text-zinc-500">Nessuna voce trovata</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
